export interface EditorContext {
    selectedText?: string;
    surroundingContext: string;
}

function nodeToPlainText(node: any): string {
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.children)) return node.children.map(nodeToPlainText).join('');
    return '';
}

function blockToLine(block: any): string {
    const text = nodeToPlainText(block).trim();
    if (!text) return '';
    const type = block.type ?? 'p';
    if (type === 'h1') return `# ${text}`;
    if (type === 'h2') return `## ${text}`;
    if (type === 'h3') return `### ${text}`;
    if (type === 'blockquote') return `> ${text}`;
    if (block.listStyleType) return `- ${text}`;
    return text;
}

export function extractEditorContext(editor: any): EditorContext {
    const result: EditorContext = { surroundingContext: '' };

    try {
        const sel = editor?.selection;
        if (!sel) return result;

        // Selected text (non-collapsed selection).
        const isCollapsed =
            JSON.stringify(sel.anchor?.path) === JSON.stringify(sel.focus?.path) && sel.anchor?.offset === sel.focus?.offset;

        if (!isCollapsed) {
            try {
                const fragment = editor.api?.fragment?.() ?? [];
                result.selectedText = fragment.map(nodeToPlainText).join('\n').trim();
            } catch {
                // fragment() may throw if selection is invalid.
            }
        }

        // Surrounding context: current block + up to 4 blocks before/after.
        const children: any[] = editor.children ?? [];
        const cursorPath = sel.anchor?.path ?? [];
        const blockIndex = cursorPath[0] ?? 0;

        const start = Math.max(0, blockIndex - 4);
        const end = Math.min(children.length, blockIndex + 5);
        const contextLines: string[] = [];

        for (let i = start; i < end; i++) {
            const line = blockToLine(children[i]);
            if (line) contextLines.push(line);
        }
        result.surroundingContext = contextLines.join('\n');
    } catch {
        // Safety net — never crash the caller.
    }

    return result;
}
